import { DocumentStatus } from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

/**
 * Formats milliseconds into a human-readable time string.
 * Returns format like "2m 30s" for times over 60 seconds, or "45s" for shorter durations.
 */
export const formatMilliseconds = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

/**
 * Calculates the processing time between document upload and processing completion.
 * Returns a human-readable string format (e.g., "2m 30s" or "45s").
 * Returns "N/A" if dates are missing, invalid, or if processed time is before upload time.
 */
export const calculateProcessingTime = (
  uploadedDatetime?: string | Date | null,
  processedDatetime?: string | Date | null
): string => {
  if (!uploadedDatetime || !processedDatetime) {
    return "N/A";
  }

  const uploadTime = new Date(uploadedDatetime).getTime();
  const processTime = new Date(processedDatetime).getTime();

  // Handle invalid dates or future dates
  if (isNaN(uploadTime) || isNaN(processTime) || processTime < uploadTime) {
    return "N/A";
  }

  const diffInMs = processTime - uploadTime;
  return formatMilliseconds(diffInMs);
};

/**
 * Reviewer-facing label for a document category / record type key. The app
 * spells the claims product "Stop-Loss Claims" (client reporting, metrics,
 * summary tables); a naive split-and-capitalise gives "Stoploss Claims".
 */
const CATEGORY_LABELS: Record<string, string> = {
  stoploss_claims: "Stop-Loss Claims",
  stoploss_claim: "Stop-Loss Claim",
};

export const formatDocumentCategory = (key: string | null | undefined): string => {
  if (!key) return "";
  // "medical_stoploss_claims" → "Medical Stop-Loss Claims": the product word is
  // hyphenated and capitalised wherever the app writes it.
  // formatToTitleCase keeps the app's acronym casing ("eor" → "EOR").
  return CATEGORY_LABELS[key] ?? formatToTitleCase(key).replace(/\bStoploss\b/g, "Stop-Loss");
};

/**
 * Formats a snake_case or lowercase string into Title Case for display.
 * Example: "stop_loss_claim" becomes "Stop Loss Claim"
 */
export const formatRecordType = (type: string): string => {
  if (!type) return "";
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Formats a status string by capitalizing the first letter.
 * Example: DocumentStatus.PENDING becomes "Pending"
 */
export const formatStatus = (status?: DocumentStatus): string => {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1);
};
